import React from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { useOrder } from '../context/OrderContext';
import { useSettings } from '../context/SettingsContext';
import { getOptimizedImageUrl } from '../utils';

export function Hero() {
  const { scrollY } = useScroll();
  const yParallax = useTransform(scrollY, [0, 1000], [0, 250]);
  const opacityParallax = useTransform(scrollY, [0, 600], [1, 0]);
  const { openOrderModal } = useOrder();
  const { settings } = useSettings();
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const heroImage = settings.hero_image_url;
  const heroVideoWebm = settings.hero_video_webm_url;
  const heroVideoMp4 = settings.hero_video_mp4_url;
  const hasVideo = heroVideoWebm || heroVideoMp4;

  React.useEffect(() => {
    if (videoRef.current && hasVideo) {
      videoRef.current.defaultMuted = true;
      videoRef.current.muted = true;
      
      // Force play with a slight delay for some mobile browsers
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          console.log("Autoplay failed, retrying on first interaction:", e);
          // Add a one-time event listener to play as soon as the user touches the screen
          const playOnTouch = () => {
            if (videoRef.current) {
              videoRef.current.play();
            }
            document.removeEventListener('touchstart', playOnTouch);
            document.removeEventListener('click', playOnTouch);
          };
          document.addEventListener('touchstart', playOnTouch, { once: true });
          document.addEventListener('click', playOnTouch, { once: true });
        });
      }
    }
  }, [hasVideo, heroVideoWebm, heroVideoMp4]);

  // High-end cinematic easing
  const customEase = [0.16, 1, 0.3, 1];

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const textRevealVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: customEase },
    },
  };

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: customEase },
    },
  };

  return (
    <section id="home" className="relative h-screen min-h-[600px] w-full overflow-hidden bg-gusto-dark flex items-end md:items-center">
      {/* Background Video */}
      <motion.div
        className="absolute inset-0 z-0 origin-center"
        initial={{ scale: 1.05 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.2, ease: customEase }}
      >
        {hasVideo ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
            poster={heroImage ? getOptimizedImageUrl(heroImage, 1024) : "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1024&q=70"}
            className="w-full h-full object-cover"
          >
            {heroVideoWebm && <source src={heroVideoWebm} type="video/webm" />}
            {heroVideoMp4 && <source src={heroVideoMp4} type="video/mp4" />}
          </video>
        ) : (
          <picture>
            <source 
              media="(max-width: 640px)" 
              srcSet={heroImage ? getOptimizedImageUrl(heroImage, 800) : "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=800&q=70"} 
            />
            <source 
              media="(max-width: 1024px)" 
              srcSet={heroImage ? getOptimizedImageUrl(heroImage, 1200) : "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1200&q=80"} 
            />
            <img 
              src={heroImage ? getOptimizedImageUrl(heroImage, 2000) : "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=2000&q=80"} 
              alt="Hero Background" 
              className="w-full h-full object-cover"
              fetchPriority="high"
              loading="eager"
            />
          </picture>
        )}
        {/* Soft Scrim (Gradient) - Applies darkening ONLY behind the text area instead of total image covering */}
        <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#1B1918]/90 via-[#1B1918]/40 to-transparent mix-blend-multiply" />
      </motion.div>

      {/* Content container aligned bottom on mobile, left on desktop */}
      <motion.div
        className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pb-10 md:pb-0 pt-24 select-none flex"
        style={{ y: yParallax, opacity: opacityParallax }}
      >
        {/* Glassmorphism Text Container */}
        <div className="relative overflow-hidden rounded-[24px] md:rounded-[40px] border border-white/10 bg-white/5 backdrop-blur-[16px] p-6 sm:p-10 md:p-14 lg:p-16 max-w-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          {/* Subtle light leak effect inside the glass */}
          <div className="absolute -top-[150px] -left-[150px] w-[400px] h-[400px] bg-white/10 blur-[80px] rounded-full pointer-events-none" />
          
          <div className="relative z-10 flex flex-col items-start text-left gap-2 md:gap-3 mb-6 md:mb-8">
            <span 
              className="font-serif italic text-gusto-gold uppercase tracking-[0.2em] text-xs sm:text-sm font-medium mb-2"
            >
              {settings.hero_label}
            </span>

            {/* Smooth text reveals */}
            <div className="w-full">
              <h1 className="font-heading font-bold text-white leading-[1.05] tracking-tight" style={{ fontSize: 'clamp(2.5rem, 8vw, 5.5rem)' }}>
                {settings.hero_title_1}
              </h1>
            </div>
            <div className="w-full">
              <h1 className="font-heading font-bold text-white leading-[1.05] tracking-tight" style={{ fontSize: 'clamp(2.5rem, 8vw, 5.5rem)' }}>
                {settings.hero_title_2}
              </h1>
            </div>
          </div>

          <p
            className="relative z-10 font-sans text-white/80 text-base md:text-xl max-w-lg leading-relaxed mb-8 md:mb-10 font-medium"
          >
            {settings.hero_subtitle}
          </p>

          <div
            className="relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-4"
          >
            <a
              href="#menu"
              className="w-full sm:w-auto text-center px-8 py-3.5 md:px-10 md:py-4 bg-gusto-red text-white font-medium rounded-full text-base transition duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-gusto-red/30"
            >
              {settings.hero_btn_1}
            </a>
            
            <button
              onClick={openOrderModal}
              className="w-full sm:w-auto text-center px-8 py-3.5 md:px-10 md:py-4 bg-white/10 backdrop-blur-md border border-white/20 text-white font-medium rounded-full text-base transition duration-300 hover:bg-white hover:text-gusto-dark hover:scale-105"
            >
              {settings.hero_btn_2}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator - Moved towards bottom center across screens */}
      <motion.div
        className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-10 text-white/50 hidden md:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 1.5 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown className="w-8 h-8 opacity-70" />
        </motion.div>
      </motion.div>
    </section>
  );
}
